import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'
import { serializeQueryWithArrayBrackets } from '@/utils/querySerializer'
import { isIpAddress, UUID_RE } from '@/utils/searchFilters'

export interface SearchResult {
  id: string
  label: string
  description?: string
  category: string
  route: string
}

const NAVIGATION_ITEMS: SearchResult[] = [
  {
    id: 'nav-dashboard',
    label: 'Dashboard',
    category: 'Dashboard',
    route: '/',
  },
  { id: 'nav-users', label: 'Users', category: 'Users', route: '/users' },
  {
    id: 'nav-user-groups',
    label: 'User Groups',
    category: 'Users',
    route: '/user-groups',
  },
  {
    id: 'nav-invitations',
    label: 'Invitations',
    category: 'Users',
    route: '/invitations',
  },
  {
    id: 'nav-compute',
    label: 'Compute',
    category: 'Public Cloud',
    route: '/compute',
  },
  {
    id: 'nav-storage',
    label: 'Storage',
    category: 'Public Cloud',
    route: '/storage',
  },
  {
    id: 'nav-network',
    label: 'Network',
    category: 'Public Cloud',
    route: '/network',
  },
  { id: 'nav-vpcs', label: 'VPCs', category: 'Public Cloud', route: '/vpcs' },
  {
    id: 'nav-subnets',
    label: 'Subnets',
    category: 'Public Cloud',
    route: '/subnets',
  },
  {
    id: 'nav-clusters',
    label: 'Clusters',
    category: 'Public Cloud',
    route: '/clusters',
  },
  { id: 'nav-prices', label: 'Prices', category: 'Billing', route: '/prices' },
  {
    id: 'nav-prices-vm',
    label: 'Prices — VM',
    category: 'Billing',
    route: '/prices?tab=vm',
  },
  {
    id: 'nav-prices-storage',
    label: 'Prices — Storage',
    category: 'Billing',
    route: '/prices?tab=storage',
  },
  {
    id: 'nav-prices-ip',
    label: 'Prices — Public IP',
    category: 'Billing',
    route: '/prices?tab=public-ip',
  },
  {
    id: 'nav-promo-codes',
    label: 'Promo Codes',
    category: 'Marketing',
    route: '/promo-codes',
  },
]

function filterNavigation(query: string): SearchResult[] {
  if (!query.trim()) return NAVIGATION_ITEMS
  const q = query.toLowerCase()
  return NAVIGATION_ITEMS.filter((item) => item.label.toLowerCase().includes(q))
}

async function searchUserById(
  id: string,
  signal: AbortSignal,
): Promise<SearchResult[]> {
  const result = await client.GET('/v1/admin/users/{user_id}', {
    params: { path: { user_id: id } },
    signal,
  })
  if (!result.data) return []
  const user = result.data as { id: string; email?: string }
  return [
    {
      id: user.id,
      label: user.email ?? user.id,
      category: 'Users',
      route: `/users/${user.id}`,
    },
  ]
}

async function searchPromoCodeById(
  id: string,
  signal: AbortSignal,
): Promise<SearchResult[]> {
  const result = await client.GET('/v1/admin/promo_codes/{promo_code_id}', {
    params: { path: { promo_code_id: id } },
    signal,
  })
  if (!result.data) return []
  const code = result.data as { id: string; code?: string }
  return [
    {
      id: code.id,
      label: code.code ?? code.id,
      category: 'Promo Codes',
      route: `/promo-codes/${code.id}`,
    },
  ]
}

interface ListItem {
  id: string
  name?: string
}

interface PaginatedResponse {
  items: ListItem[]
  pagination: { totalRecords: number }
}

const LIST_RESOURCES = [
  { endpoint: '/v2/admin/vms', category: 'Compute', route: '/compute' },
  { endpoint: '/v1/admin/storages', category: 'Storage', route: '/storage' },
  {
    endpoint: '/v1/admin/public_ips',
    category: 'Network',
    route: '/network',
  },
  { endpoint: '/v1/admin/vpcs', category: 'VPCs', route: '/vpcs' },
  { endpoint: '/v1/admin/subnets', category: 'Subnets', route: '/subnets' },
] as const

async function searchListResourceById(
  endpoint: (typeof LIST_RESOURCES)[number]['endpoint'],
  id: string,
  category: string,
  route: string,
  signal: AbortSignal,
): Promise<SearchResult[]> {
  const result = await client.GET(endpoint, {
    params: { query: { ids: [id], page: 1, perPage: 1 } as never },
    querySerializer: serializeQueryWithArrayBrackets,
    signal,
  })
  if (!result.data) return []
  const data = result.data as PaginatedResponse
  return (data.items ?? []).map((item) => ({
    id: item.id,
    label: item.name ?? item.id,
    category,
    route: `${route}/${item.id}`,
  }))
}

async function searchByUuid(
  uuid: string,
  signal: AbortSignal,
): Promise<SearchResult[]> {
  const searches: Promise<SearchResult[]>[] = [
    searchUserById(uuid, signal),
    ...LIST_RESOURCES.map((r) =>
      searchListResourceById(r.endpoint, uuid, r.category, r.route, signal),
    ),
    searchPromoCodeById(uuid, signal),
  ]

  const results = await Promise.allSettled(searches)
  return results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
}

async function searchByIp(
  address: string,
  signal: AbortSignal,
): Promise<SearchResult[]> {
  const result = await client.GET('/v1/admin/lookup/ip/{address}', {
    params: { path: { address } },
    signal,
  })
  // 404 means no public IP holds this address — an empty result, not an error.
  if (result.response.status === 404) return []
  const { publicIp, vm, owner } = unwrapApiData(result, 'IP lookup failed')
  const results: SearchResult[] = [
    {
      id: publicIp.id,
      label: publicIp.address ?? publicIp.name,
      description: publicIp.name,
      category: 'Network',
      route: `/network/${publicIp.id}`,
    },
  ]
  if (vm) {
    results.push({
      id: vm.id,
      label: vm.name,
      description: 'Attached VM',
      category: 'Compute',
      route: `/compute/${vm.id}`,
    })
  }
  if (owner) {
    results.push({
      id: owner.userId,
      label: owner.email,
      description: 'Owner',
      category: 'Users',
      route: `/users/${owner.userId}`,
    })
  }
  return results
}

async function searchUsersByEmail(
  query: string,
  signal: AbortSignal,
): Promise<SearchResult[]> {
  const result = await client.GET('/v2/admin/users', {
    params: { query: { email: query, page: 1, perPage: 10 } },
    querySerializer: serializeQueryWithArrayBrackets,
    signal,
  })
  if (!result.data) return []
  const data = result.data as {
    items: Array<{ id: string; email?: string }>
    pagination: { totalRecords: number }
  }
  return (data.items ?? []).map((user) => ({
    id: user.id,
    label: user.email ?? user.id,
    category: 'Users',
    route: `/users/${user.id}`,
  }))
}

async function searchPromoCodesByCode(
  query: string,
  signal: AbortSignal,
): Promise<SearchResult[]> {
  const result = await client.GET('/v1/admin/promo_codes', {
    params: { query: { code: query, page: 1, perPage: 5 } as never },
    querySerializer: serializeQueryWithArrayBrackets,
    signal,
  })
  if (!result.data) return []
  const data = result.data as {
    items: Array<{ id: string; code?: string }>
    pagination: { totalRecords: number }
  }
  return (data.items ?? []).map((code) => ({
    id: code.id,
    label: code.code ?? code.id,
    category: 'Promo Codes',
    route: `/promo-codes/${code.id}`,
  }))
}

async function searchByText(
  query: string,
  signal: AbortSignal,
): Promise<SearchResult[]> {
  const results = await Promise.allSettled([
    searchUsersByEmail(query, signal),
    searchPromoCodesByCode(query, signal),
  ])
  return results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
}

export function useGlobalSearch(query: string) {
  const [apiResults, setApiResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController>()
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const navResults = useMemo(() => filterNavigation(query.trim()), [query])

  const doSearch = useCallback(async (q: string, signal: AbortSignal) => {
    try {
      let results: SearchResult[]
      if (isIpAddress(q)) {
        results = await searchByIp(q, signal)
      } else if (UUID_RE.test(q)) {
        results = await searchByUuid(q, signal)
      } else if (q.length >= 2) {
        results = await searchByText(q, signal)
      } else {
        results = []
      }
      if (!signal.aborted) setApiResults(results)
    } catch (e) {
      if (signal.aborted) return
      setApiResults([])
      setError(e instanceof Error ? e.message : 'Search failed')
    } finally {
      if (!signal.aborted) setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    abortRef.current?.abort()
    clearTimeout(debounceRef.current)

    setError(null)
    const trimmed = query.trim()
    if (!trimmed) {
      setApiResults([])
      setIsLoading(false)
      return
    }

    const controller = new AbortController()
    abortRef.current = controller

    const isExact = UUID_RE.test(trimmed) || isIpAddress(trimmed)
    const delay = isExact ? 100 : 300

    setIsLoading(true)
    debounceRef.current = setTimeout(() => {
      void doSearch(trimmed, controller.signal)
    }, delay)

    return () => {
      clearTimeout(debounceRef.current)
      controller.abort()
    }
  }, [query, doSearch])

  const results = useMemo(
    () => [...navResults, ...apiResults],
    [navResults, apiResults],
  )

  return { results, isLoading, error }
}
